import { Globe, Trash2 } from 'lucide-react'
import { useState, type ReactNode, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Spinner,
  SpinnerIcon,
} from '@/components/ui'

import {
  discoveryStatusBadgeVariant,
  discoveryStatusLabelKey,
  isDiscoveryRunning,
} from '../discovery'
import {
  useDeleteDiscoverySource,
  useDiscoverySources,
  useStartDiscovery,
  useUpdateWebsite,
} from '../queries'
import { asNumber, type WebsiteDiscovery } from '../types'

type StartMutation = ReturnType<typeof useStartDiscovery>
type DeleteMutation = ReturnType<typeof useDeleteDiscoverySource>

/** Aceita apenas URLs http(s) — mesmo criterio do backend (`invalid_url`), como feedback imediato no cliente. */
function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim())
}

/**
 * Formulario de "adicionar site" quando o tenant pulou o site no onboarding (empty-state = action-state):
 * salva o site (PATCH) e ja dispara o aprendizado do site cadastrado. Compartilha a mutation de start do
 * painel para que, ao concluir, o erro `website_not_set` limpe e o painel volte a mostrar a lista de fontes.
 */
function WebsiteMissingForm({
  tenantId,
  startMutation,
}: {
  readonly tenantId: string
  readonly startMutation: StartMutation
}) {
  const { t } = useTranslation()
  const [website, setWebsite] = useState('')
  const updateWebsiteMutation = useUpdateWebsite(tenantId)

  const invalidWebsite =
    updateWebsiteMutation.error instanceof ApiError &&
    updateWebsiteMutation.error.problem.errorCode === 'invalid_website'

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = website.trim()
    if (trimmed.length === 0) {
      return
    }

    updateWebsiteMutation.mutate(trimmed, {
      onSuccess: () => {
        startMutation.mutate(undefined)
      },
    })
  }

  const pending = updateWebsiteMutation.isPending || startMutation.isPending

  return (
    <div className="flex flex-col gap-3">
      <Alert variant="warning" title={t('knowledge.discovery.websiteMissing.title')}>
        {t('knowledge.discovery.websiteMissing.description')}
      </Alert>
      <form
        onSubmit={(event) => {
          submit(event)
        }}
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
      >
        <Input
          id="discovery-website"
          label={t('knowledge.discovery.websiteMissing.label')}
          placeholder={t('knowledge.discovery.websiteMissing.placeholder')}
          value={website}
          onChange={(event) => {
            setWebsite(event.target.value)
          }}
          error={invalidWebsite ? t('knowledge.discovery.websiteMissing.invalid') : undefined}
          className="sm:flex-1"
        />
        <Button type="submit" loading={pending} disabled={website.trim().length === 0}>
          {t('knowledge.discovery.websiteMissing.cta')}
        </Button>
      </form>
    </div>
  )
}

interface DiscoverySourceItemProps {
  readonly source: WebsiteDiscovery
  readonly deleteMutation: DeleteMutation
}

/**
 * Uma fonte da lista: URL (ou "site cadastrado" quando sem url), status e progresso, com remocao confirmada
 * inline (troca a lixeira pela pergunta + [Sim, remover]/[Cancelar]). O poll do painel avanca o status sozinho.
 */
function DiscoverySourceItem({ source, deleteMutation }: DiscoverySourceItemProps) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [failed, setFailed] = useState(false)

  const label = source.url ?? t('knowledge.discovery.sources.registeredSite')
  const running = isDiscoveryRunning(source.status)
  const canRemove = source.id !== null
  const isDeleting =
    deleteMutation.isPending && source.id !== null && deleteMutation.variables === source.id

  // Linha secundaria: progresso ("N encontradas/aprendidas") ou mensagem de falha, em vocabulario neutro.
  let progress: string
  if (source.status === 'Crawling') {
    progress = t('knowledge.discovery.progress.discovered', {
      pages: asNumber(source.pagesDiscovered),
    })
  } else if (source.status === 'Failed') {
    progress = t('knowledge.discovery.sources.failed')
  } else {
    progress = t('knowledge.discovery.progress.learned', { pages: asNumber(source.pagesIndexed) })
  }

  const remove = () => {
    if (source.id === null) {
      return
    }
    setFailed(false)
    deleteMutation.mutate(source.id, {
      onSuccess: () => {
        setConfirming(false)
      },
      // Sem onError a remocao falharia em silencio (a fonte continua na lista sem explicacao).
      onError: () => {
        setFailed(true)
      },
    })
  }

  return (
    <li className="flex flex-col gap-1 rounded-md border border-border-default px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Globe aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground-muted" />
          <span className="truncate text-sm text-foreground-primary" title={label}>
            {label}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {running ? <SpinnerIcon size="sm" className="text-action-primary" /> : null}
          <Badge variant={discoveryStatusBadgeVariant(source.status)}>
            {t(discoveryStatusLabelKey(source.status))}
          </Badge>
          {canRemove ? (
            confirming ? (
              <div className="flex items-center gap-1">
                <Button size="sm" variant="danger" loading={isDeleting} onClick={remove}>
                  {t('knowledge.discovery.source.confirm')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isDeleting}
                  onClick={() => {
                    setConfirming(false)
                  }}
                >
                  {t('knowledge.discovery.source.cancel')}
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setConfirming(true)
                }}
                aria-label={t('knowledge.discovery.source.remove', { url: label })}
                className="rounded text-foreground-muted transition-colors hover:text-status-danger focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
              >
                <Trash2 aria-hidden="true" className="h-4 w-4" />
              </button>
            )
          ) : null}
        </div>
      </div>
      <p className="text-xs text-foreground-muted">
        {confirming ? t('knowledge.discovery.source.confirmQuestion') : progress}
      </p>
      {failed ? (
        <p className="text-xs text-status-danger">{t('knowledge.discovery.source.error')}</p>
      ) : null}
    </li>
  )
}

/**
 * "Ensine seu agente" (SS-02; docs/design/09). O agente aprende com N fontes (uma por URL): um campo para
 * adicionar uma URL e a lista de fontes ja adicionadas, cada uma com status/progresso e remocao. Empty state
 * = action state: sem fontes, oferece adicionar a primeira ou aprender com o site cadastrado no onboarding.
 * Vocabulario neutro — nunca expoe crawler/embeddings/RAG. O Playground segue acessivel enquanto indexa.
 */
export function DiscoveryPanel({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation()
  const sourcesQuery = useDiscoverySources(tenantId)
  const startMutation = useStartDiscovery(tenantId)
  const deleteMutation = useDeleteDiscoverySource(tenantId)

  const [url, setUrl] = useState('')
  const [clientUrlError, setClientUrlError] = useState(false)

  const websiteMissing =
    startMutation.error instanceof ApiError &&
    startMutation.error.problem.errorCode === 'website_not_set'

  const invalidUrlFromServer =
    startMutation.error instanceof ApiError &&
    startMutation.error.problem.errorCode === 'invalid_url'

  const addUrl = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = url.trim()
    if (!isHttpUrl(trimmed)) {
      setClientUrlError(true)
      return
    }
    setClientUrlError(false)
    startMutation.mutate(trimmed, {
      onSuccess: () => {
        setUrl('')
      },
    })
  }

  const learnFromSite = () => {
    startMutation.mutate(undefined)
  }

  // Site do onboarding ausente: guia o cadastro do site (e ja dispara o aprendizado) antes de qualquer fonte.
  if (websiteMissing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('knowledge.discovery.title')}</CardTitle>
          <CardDescription>{t('knowledge.discovery.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <WebsiteMissingForm tenantId={tenantId} startMutation={startMutation} />
        </CardContent>
      </Card>
    )
  }

  const addForm = (
    <form
      onSubmit={(event) => {
        addUrl(event)
      }}
      className="flex flex-col gap-2 sm:flex-row sm:items-end"
    >
      <Input
        id="discovery-add-url"
        label={t('knowledge.discovery.add.label')}
        placeholder={t('knowledge.discovery.add.placeholder')}
        value={url}
        onChange={(event) => {
          setUrl(event.target.value)
          setClientUrlError(false)
        }}
        error={
          clientUrlError || invalidUrlFromServer
            ? t('knowledge.discovery.add.invalidUrl')
            : undefined
        }
        className="sm:flex-1"
      />
      <Button type="submit" loading={startMutation.isPending} disabled={url.trim().length === 0}>
        {t('knowledge.discovery.add.cta')}
      </Button>
    </form>
  )

  let sourcesArea: ReactNode
  if (sourcesQuery.isPending) {
    sourcesArea = <Spinner label={t('loading')} />
  } else if (sourcesQuery.isError) {
    sourcesArea = (
      <div className="flex flex-col items-start gap-3">
        <Alert variant="danger" title={t('knowledge.discovery.loadError')} />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void sourcesQuery.refetch()
          }}
        >
          {t('knowledge.discovery.retry')}
        </Button>
      </div>
    )
  } else if (sourcesQuery.data.length === 0) {
    sourcesArea = (
      <EmptyState
        icon={Globe}
        title={t('knowledge.discovery.empty.title')}
        description={t('knowledge.discovery.empty.description')}
        action={
          <Button variant="secondary" onClick={learnFromSite} loading={startMutation.isPending}>
            {t('knowledge.discovery.empty.learnFromSite')}
          </Button>
        }
      />
    )
  } else {
    sourcesArea = (
      <ul className="flex flex-col gap-2" aria-label={t('knowledge.discovery.sources.title')}>
        {sourcesQuery.data.map((source, index) => (
          <DiscoverySourceItem
            key={source.id ?? source.url ?? `source-${String(index)}`}
            source={source}
            deleteMutation={deleteMutation}
          />
        ))}
      </ul>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('knowledge.discovery.title')}</CardTitle>
        <CardDescription>{t('knowledge.discovery.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {addForm}
        {sourcesArea}
      </CardContent>
    </Card>
  )
}
